import { useState, type Ref } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Link } from 'react-router';

import type { GroupedMenuItems } from '~/packages/eds/src/components/Menu/types';
import type { Action } from '~/packages/eds/src/types/Action';

import { getUserName } from '~/eds/src/utils/getUserName';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { usePermissions } from '~/hooks/usePermissions';
import { Avatar } from '~/packages/eds/src/components/Avatar/Avatar';
import { Icon } from '~/packages/eds/src/components/Icon/Icon';
import { Layout } from '~/packages/eds/src/components/Layout/Layout';
import { Menu } from '~/packages/eds/src/components/Menu/Menu';
import { Text } from '~/packages/eds/src/components/Text/Text';
import { NavMenu } from '~/packages/eds/src/layouts/AppNav/SideNav/subcomponents/NavMenu/NavMenu';
import { RbacPermission } from '~/types/Rbac';
import { logout } from '~/utils/logout';

import { SidebarNavOptOutModal } from './SidebarNavOptOutModal';
import './UserProfileMenu.css';

type FlyoutEntry =
  | { type: 'divider'; id: string }
  | { type: 'item'; id: string; action: Action };

function toFlyoutEntries(groups: GroupedMenuItems[]): FlyoutEntry[] {
  return groups.flatMap((group, groupIndex) => [
    ...(groupIndex > 0
      ? [{ type: 'divider' as const, id: `divider-${groupIndex}` }]
      : []),
    ...group.items.map((action, itemIndex) => ({
      type: 'item' as const,
      id: `${groupIndex}-${itemIndex}`,
      action,
    })),
  ]);
}

function ProfileNavMenuContent({
  fullName,
  email,
  entries,
  close,
}: {
  fullName: string;
  email: string;
  entries: FlyoutEntry[];
  close: () => void;
}) {
  return (
    <>
      <div className="user-profile-nav-menu-header">
        <Text as="p" variant="body-semi-m">
          {fullName}
        </Text>
        <Text as="p" tone="subdued" variant="body-s">
          {email}
        </Text>
      </div>
      <div className="user-profile-nav-menu-divider" />
      <ul className="user-profile-nav-menu-list">
        {entries.map((entry) => {
          if (entry.type === 'divider') {
            return (
              <li key={entry.id} className="user-profile-nav-menu-divider" />
            );
          }

          const { action } = entry;
          const itemContent = (
            <Layout align="center" spacing="s">
              {action.icon && <Icon icon={action.icon.key} size="s" />}
              <Text>{action.label}</Text>
            </Layout>
          );
          // Every item closes the menu after acting on it — matching Menu's
          // behavior. NavMenu was built for rail sublinks, where navigating
          // away happens to close it as a side effect; neither an in-place
          // action (Log out, Switch to Old Menu) nor a same-app client-side
          // navigation (Account, My Deleted Items) does that on its own.
          const handleActivate = () => {
            action.onClick?.();
            close();
          };

          return (
            <li key={entry.id}>
              {action.href ? (
                <Link
                  className="user-profile-nav-menu-item"
                  data-eds-nav-menu-item
                  onClick={handleActivate}
                  to={action.href}
                >
                  {itemContent}
                </Link>
              ) : (
                <button
                  className="user-profile-nav-menu-item"
                  data-eds-nav-menu-item
                  onClick={handleActivate}
                  type="button"
                >
                  {itemContent}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
}

const messages = defineMessages({
  userProfileAriaLabel: {
    id: 'AppHeaderActions.userProfileAriaLabel',
    defaultMessage: 'User profile',
    description: 'Accessible label for the user profile menu trigger.',
  },
  account: {
    id: 'UserProfileMenu.account',
    defaultMessage: 'Account',
    description: 'Label for the Account menu action',
  },
  deletedItems: {
    id: 'PageHeader.deletedItems',
    defaultMessage: 'My Deleted Items',
    description: 'Label for the My Deleted Items menu action',
  },
  switchToOldMenu: {
    id: 'UserProfileMenu.switchToOldMenu',
    defaultMessage: 'Switch to Old Menu',
    description:
      'Label for the menu action that opens the switch-to-classic-navigation confirmation.',
  },
  logOut: {
    id: 'PageHeader.logOut',
    defaultMessage: 'Log out',
    description: 'Label for the Log out menu action',
  },
});

const DOCUMENTS_VIEW_PERMISSION: RbacPermission[] = [
  { resourceId: 'documents', resourceType: 'view' },
];

export function UserProfileMenu() {
  const intl = useIntl();
  const user = useCurrentUser();
  const hasSidebarNavUxExploration = useFlag(FlagType.SidebarNav);
  const { checkPermission } = usePermissions({
    permissions: DOCUMENTS_VIEW_PERMISSION,
  });
  const hasDocumentsAccess = checkPermission('documents', 'view');

  const [isOptOutModalVisible, setIsOptOutModalVisible] = useState(false);

  const fullName = getUserName({ user, intl });

  const accountLabel = intl.formatMessage(messages.account);
  const deletedItemsLabel = intl.formatMessage(messages.deletedItems);
  const switchToOldMenuLabel = intl.formatMessage(messages.switchToOldMenu);
  const logOutLabel = intl.formatMessage(messages.logOut);

  const groupedMenuItems: GroupedMenuItems[] = [
    {
      items: [
        {
          label: accountLabel,
          text: accountLabel,
          href: '/settings',
          icon: { key: 'account-circle', position: 'left' },
        },
        ...(hasDocumentsAccess
          ? [
              {
                label: deletedItemsLabel,
                text: deletedItemsLabel,
                href: '/my-deleted-items',
                icon: { key: 'trash' as const, position: 'left' as const },
              },
            ]
          : []),
      ],
    },
    {
      items: [
        {
          label: switchToOldMenuLabel,
          text: switchToOldMenuLabel,
          icon: { key: 'undo', position: 'left' },
          onClick: () => setIsOptOutModalVisible(true),
        },
      ],
    },
    {
      items: [
        {
          label: logOutLabel,
          text: logOutLabel,
          icon: { key: 'logout', position: 'left' },
          onClick: () => logout(user),
        },
      ],
    },
  ];

  const triggerLabel = intl.formatMessage(messages.userProfileAriaLabel);

  const menu = hasSidebarNavUxExploration ? (
    <NavMenu
      content={({ close }) => (
        <ProfileNavMenuContent
          close={close}
          email={user.email}
          entries={toFlyoutEntries(groupedMenuItems)}
          fullName={fullName}
        />
      )}
      placement="bottom-end"
      trigger={
        <button
          aria-label={triggerLabel}
          className="header-icon-btn user-profile-menu-trigger user-profile-menu-trigger--exploration"
          type="button"
        >
          <Avatar name={fullName} src={user.avatar} tooltip="" />
        </button>
      }
    />
  ) : (
    <Menu
      as="button"
      groupedMenuItems={groupedMenuItems}
      header={
        <>
          <Text as="p" variant="body-semi-m">
            {fullName}
          </Text>
          <Text as="p" tone="subdued" variant="body-s">
            {user.email}
          </Text>
        </>
      }
      trigger={({ getReferenceProps, ref }) => (
        <button
          {...getReferenceProps()}
          aria-label={triggerLabel}
          className="header-icon-btn user-profile-menu-trigger"
          ref={ref as Ref<HTMLButtonElement>}
          type="button"
        >
          <Avatar name={fullName} src={user.avatar} tooltip="" />
        </button>
      )}
    />
  );

  return (
    <>
      {menu}
      <SidebarNavOptOutModal
        isVisible={isOptOutModalVisible}
        onHide={() => setIsOptOutModalVisible(false)}
      />
    </>
  );
}
